import React from 'react';
import { Zap, Sun, Battery, Thermometer, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatisticsSummary as StatisticsSummaryType } from '../types/analytics';

interface StatisticsSummaryProps {
  statistics: StatisticsSummaryType;
  comparisonStatistics?: StatisticsSummaryType;
  isComparison: boolean;
  range1Label?: string;
  range2Label?: string;
  isLoading?: boolean;
  mode?: 'power-temp' | 'energy';
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  unit: string;
  trend?: number;
  isLoading?: boolean;
}

function MetricCard({ icon, label, value, unit, trend, isLoading }: MetricCardProps) {
  if (isLoading) {
    return (
      <div className="rounded-md p-3 border border-light-grey bg-white">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 bg-light-grey rounded animate-pulse"></div>
          <div className="h-3 w-24 bg-light-grey rounded animate-pulse"></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <div className="h-6 w-16 bg-light-grey rounded animate-pulse"></div>
            <div className="h-3 w-8 bg-light-grey rounded animate-pulse"></div>
          </div>
          {trend !== undefined && (
            <div className="h-3 w-12 bg-light-grey rounded animate-pulse"></div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md p-3 border border-light-grey bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-4 h-4 bg-primary/10 rounded flex items-center justify-center">
          {icon}
        </div>
        <span className="heading-8 text-dark-blue">{label}</span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span className="heading-5 text-dark">{value?.toLocaleString(undefined, { maximumFractionDigits: 1 }) || 0}</span>
          <span className="paragraph-10 text-dark-grey">{unit}</span>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-0.5">
            {trend > 0 ? (
              <TrendingUp className="w-3 h-3 text-green" />
            ) : (
              <TrendingDown className="w-3 h-3 text-danger" />
            )}
            <span className={cn(
              "paragraph-10 font-medium",
              trend > 0 ? "text-green" : "text-danger"
            )}>
              {Math.abs(trend).toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function StatisticsSummary({ 
  statistics, 
  comparisonStatistics, 
  isComparison, 
  range1Label, 
  range2Label,
  isLoading = false,
  mode = 'energy'
}: StatisticsSummaryProps) {
  const powerTempMetrics = [
    {
      key: 'avgPower',
      icon: <Zap className="w-2.5 h-2.5 text-primary" />,
      label: 'Average Power',
      unit: 'kW'
    },
    {
      key: 'peakPower',
      icon: <TrendingUp className="w-2.5 h-2.5 text-primary" />,
      label: 'Peak Power',
      unit: 'kW'
    },
    {
      key: 'minPower',
      icon: <TrendingDown className="w-2.5 h-2.5 text-primary" />,
      label: 'Minimum Power',
      unit: 'kW'
    },
    {
      key: 'avgIndoorTemp',
      icon: <Thermometer className="w-2.5 h-2.5 text-primary" />,
      label: 'Average Indoor Temperature',
      unit: '°C'
    },
    {
      key: 'maxOutdoorTemp',
      icon: <TrendingUp className="w-2.5 h-2.5 text-primary" />,
      label: 'Max Outdoor Temperature',
      unit: '°C'
    },
    {
      key: 'minOutdoorTemp',
      icon: <TrendingDown className="w-2.5 h-2.5 text-primary" />,
      label: 'Min Outdoor Temperature',
      unit: '°C'
    }
  ];

  const energyMetrics = [
    {
      key: 'totalEnergyConsumption',
      icon: <Zap className="w-2.5 h-2.5 text-primary" />,
      label: 'Total Energy Consumption',
      unit: 'kWh'
    },
    {
      key: 'totalSolarGeneration',
      icon: <Sun className="w-2.5 h-2.5 text-primary" />,
      label: 'Total Solar Generation',
      unit: 'kWh'
    },
    {
      key: 'totalGridImport',
      icon: <Battery className="w-2.5 h-2.5 text-primary" />,
      label: 'Total Grid Import',
      unit: 'kWh'
    },
    {
      key: 'avgIndoorTempBusinessHours',
      icon: <Thermometer className="w-2.5 h-2.5 text-primary" />,
      label: 'Average Indoor Temperature (9:00 - 17:00)',
      unit: '°C'
    },
    {
      key: 'maxOutdoorTempBusinessHours',
      icon: <TrendingUp className="w-2.5 h-2.5 text-primary" />,
      label: 'Max Outdoor Temperature (9:00 - 17:00)',
      unit: '°C'
    },
    {
      key: 'minOutdoorTempBusinessHours',
      icon: <TrendingDown className="w-2.5 h-2.5 text-primary" />,
      label: 'Min Outdoor Temperature (9:00 - 17:00)',
      unit: '°C'
    }
  ];

  const metrics = mode === 'power-temp' ? powerTempMetrics : energyMetrics;

  return (
    <div className={cn(
      "flex gap-3",
      isComparison ? "flex-col lg:flex-row" : ""
    )}>
      {/* Range 1 Stats */}
      <div className="flex-1">
        <div className="bg-white rounded-lg shadow-card border border-light-grey p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {metrics.map((metric) => (
              <MetricCard
                key={metric.key}
                icon={metric.icon}
                label={metric.label}
                value={statistics?.[metric.key as keyof StatisticsSummaryType] as number || 0}
                unit={metric.unit}
                isLoading={isLoading}
              />
            ))}
          </div>
          
          {/* Summary Insights for Range 1 */}
          {!isLoading && statistics && (
            <div className="mt-4 pt-4 border-t border-light-grey">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
                {mode === 'energy' ? (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green rounded-full"></div>
                        <span className="paragraph-9 text-dark-grey">
                          Solar Efficiency: {statistics.totalSolarGeneration > 0 && statistics.totalEnergyConsumption > 0 
                            ? ((statistics.totalSolarGeneration / statistics.totalEnergyConsumption) * 100).toFixed(1)
                            : '0'}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span className="paragraph-9 text-dark-grey">
                          Grid Dependency: {statistics.totalGridImport > 0 && statistics.totalEnergyConsumption > 0 
                            ? ((statistics.totalGridImport / statistics.totalEnergyConsumption) * 100).toFixed(1)
                            : '0'}%
                        </span>
                      </div>
                    </div>
                    <div className="paragraph-9 text-dark-grey">
                      Temperature Range: {statistics.minOutdoorTempBusinessHours?.toFixed(1) || '0'}°C - {statistics.maxOutdoorTempBusinessHours?.toFixed(1) || '0'}°C
                    </div>
                  </>
                ) : (
                  <>
                    {/* Removed Power Range, Average Power, and Temperature Range display */}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Range 2 Stats (Comparison Mode) */}
      {isComparison && comparisonStatistics && (
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-card border border-light-grey p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {metrics.map((metric) => {
                const currentValue = comparisonStatistics?.[metric.key as keyof StatisticsSummaryType] as number || 0;
                const baseValue = statistics?.[metric.key as keyof StatisticsSummaryType] as number || 0;
                
                // Calculate percentage change
                let trend: number | undefined;
                if (baseValue > 0) {
                  trend = ((currentValue - baseValue) / baseValue) * 100;
                }

                return (
                  <MetricCard
                    key={metric.key}
                    icon={metric.icon}
                    label={metric.label}
                    value={currentValue}
                    unit={metric.unit}
                    trend={trend}
                    isLoading={isLoading}
                  />
                );
              })}
            </div>
            
            {/* Summary Insights for Range 2 */}
            {!isLoading && comparisonStatistics && (
              <div className="mt-4 pt-4 border-t border-light-grey">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
                  {mode === 'energy' ? (
                    <>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green rounded-full"></div>
                          <span className="paragraph-9 text-dark-grey">
                            Solar Efficiency: {comparisonStatistics.totalSolarGeneration > 0 && comparisonStatistics.totalEnergyConsumption > 0 
                              ? ((comparisonStatistics.totalSolarGeneration / comparisonStatistics.totalEnergyConsumption) * 100).toFixed(1)
                              : '0'}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          <span className="paragraph-9 text-dark-grey">
                            Grid Dependency: {comparisonStatistics.totalGridImport > 0 && comparisonStatistics.totalEnergyConsumption > 0 
                              ? ((comparisonStatistics.totalGridImport / comparisonStatistics.totalEnergyConsumption) * 100).toFixed(1)
                              : '0'}%
                          </span>
                        </div>
                      </div>
                      <div className="paragraph-9 text-dark-grey">
                        Temperature Range: {comparisonStatistics.minOutdoorTempBusinessHours?.toFixed(1) || '0'}°C - {comparisonStatistics.maxOutdoorTempBusinessHours?.toFixed(1) || '0'}°C
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Removed Power Range, Average Power, and Temperature Range display */}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}